/**
 * Submit sequence for the forced set-password step of an ESS invite — shared
 * by the link screen (`invite_accept_screen.tsx`) and the one-time-code screen
 * (`invite_code_screen.tsx`) through `invite_set_password_form.tsx`.
 *
 * Same order as `wagepro_login_screen.tsx`, because accepting an invite IS the
 * employee's first sign-in:
 *
 *   1. the accept mutation (the ONLY step classified with `classifyAuthError`
 *      — see the login screen for why a later `TypeError` must not read as a
 *      network failure);
 *   2. `performCookieLoginPostAction` — drop any stale bearer pair;
 *   3. `persistNativeSession` — in the native shell the body-carried token IS
 *      the session (the cross-site cookie is dropped by the WebView). Missing
 *      token or a store failure → error, and we do NOT navigate (that would
 *      boot into a 401 loop). On web this is a no-op `true`;
 *   4. `onAccepted`, then navigate to `/`.
 *
 * The password never leaves this call: nothing here writes to any storage
 * other than the native session token (`native_session_store.ts`).
 */

import {
  classifyAuthError,
  type AuthErrorDisplay,
} from './auth_error_display.js';
import {
  persistNativeSession,
  type NativeSessionCarrier,
} from './native_shell_auth.js';
import type { InviteAcceptRequest } from './invite_accept_logic.js';

export interface InviteAcceptSubmitDeps {
  /** Runs the accept mutation named by the request. */
  submit: (request: InviteAcceptRequest) => Promise<NativeSessionCarrier>;
  /** `performCookieLoginPostAction` with the real token store in production. */
  afterLogin: () => Promise<void>;
  native: boolean;
  setToken: (token: string) => Promise<void>;
  onAccepted?: () => void;
  navigate: (path: string) => void;
}

export type InviteAcceptSubmitOutcome =
  | { kind: 'done' }
  | { kind: 'error'; error: AuthErrorDisplay };

export async function submitInviteAccept(
  request: InviteAcceptRequest,
  deps: InviteAcceptSubmitDeps,
): Promise<InviteAcceptSubmitOutcome> {
  let result: NativeSessionCarrier;
  try {
    result = await deps.submit(request);
  } catch (err) {
    return { kind: 'error', error: classifyAuthError(err) };
  }

  await deps.afterLogin();

  let persisted: boolean;
  try {
    persisted = await persistNativeSession(result, {
      native: deps.native,
      setToken: deps.setToken,
    });
  } catch (err) {
    console.error('[wagepro-web] storing the native session failed:', err);
    persisted = false;
  }
  if (!persisted) {
    return { kind: 'error', error: { kind: 'unknown' } };
  }

  deps.onAccepted?.();
  deps.navigate('/');
  return { kind: 'done' };
}
